import React, { forwardRef, useState } from 'react';
import PropTypes from 'prop-types';

import { Carousel } from '../base';
import usePrefersReducedMotion from '../hooks/usePrefersReducedMotion';

const AUTOPLAY_INTERVAL = 8000;

// Shared shell for the homepage hero carousels. Apps supply the slides
// (see homeCarousel.js in each app), this component owns autoplay and
// navigation behavior.
const HeroCarousel = forwardRef((props, ref) => {
  const totalSlides = React.Children.count(props.children);

  const enabled = totalSlides > 1;

  // WCAG 2.2.2: autoplay pauses on hover/focus and stops for good once the
  // user navigates manually (dots, arrows, swipe) or prefers reduced motion.
  // Hover and focus are tracked separately so leaving one doesn't cancel
  // a pause held by the other.
  const [hovered, setHovered] = useState(false);
  const [focusWithin, setFocusWithin] = useState(false);
  const [stopped, setStopped] = useState(false);
  const reducedMotion = usePrefersReducedMotion();

  const isPlaying = enabled && !hovered && !focusWithin && !stopped && !reducedMotion;

  return (
    <Carousel
      ref={ref}
      className="carousel-hero"
      totalSlides={totalSlides}
      isPlaying={isPlaying}
      interval={AUTOPLAY_INTERVAL}
      infinite={enabled}
      touchEnabled={enabled}
      dragEnabled={enabled}
    >
      <div
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocusCapture={() => setFocusWithin(true)}
        onBlurCapture={event => {
          // Ignore focus moving between elements inside the carousel
          if (!event.currentTarget.contains(event.relatedTarget)) {
            setFocusWithin(false);
          }
        }}
        onPointerDownCapture={() => setStopped(true)}
        onKeyDownCapture={event => {
          // Keyboard activation of dots/arrows stops autoplay like a click
          if (event.key === 'Enter' || event.key === ' ') {
            setStopped(true);
          }
        }}
      >
        <Carousel.Slider classNameAnimation="homeCarouselAnimation">
          {props.children}
        </Carousel.Slider>
        {enabled &&
          (props.controls ? (
            props.controls({ totalSlides })
          ) : (
            <>
              <div className="carousel-controls-container">
                <Carousel.ButtonBack className="carousel-control-prev">
                  <span className="sr-only">Previous slide</span>
                </Carousel.ButtonBack>
                <Carousel.ButtonNext className="carousel-control-next">
                  <span className="sr-only">Next slide</span>
                </Carousel.ButtonNext>
              </div>
              <Carousel.DotGroup />
            </>
          ))}
      </div>
    </Carousel>
  );
});

HeroCarousel.propTypes = {
  children: PropTypes.node,
  // Replaces the default arrows + dots, called with { totalSlides } when there are 2+ slides.
  // Rendered inside the carousel, so Carousel.Dot and Carousel.Context work.
  controls: PropTypes.func,
};

export default HeroCarousel;
